import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Message,
  MessageAuthor,
  MessageType,
  PushMessage,
  UpdateMessage,
} from "../types";

export type ChatMessageState = {
  messages: Message[];
  pushMessage: (message: PushMessage) => string;
  updateMessage: (message: UpdateMessage) => void;
  latestBotMessageId: string;
  conversationId: string;
  reset: (conversationId?: string) => void;
};

export const defaultChatMessageState: ChatMessageState = {
  messages: [],
  pushMessage: () => "",
  updateMessage: () => null,
  latestBotMessageId: "",
  conversationId: "",
  reset: () => null,
};

export const ChatMessageState = createContext<ChatMessageState>(
  defaultChatMessageState
);

export const ChatStateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [latestBotMessageId, setLatestBotMessageId] = useState<string>("");
  const [conversationId, setConversationId] = useState(uuidv4());

  const pushMessage = useCallback(
    (message: PushMessage): string => {
      const id = uuidv4();
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          ...message,
          id,
          type: MessageType.Text,
          sentAt: new Date().toISOString(),
        },
      ]);
      if (message.author === MessageAuthor.Bot) {
        setLatestBotMessageId(id);
      }
      return id;
    },
    [setMessages, setLatestBotMessageId]
  );

  const updateMessage = useCallback(
    ({ id, text, widget, question }: UpdateMessage) => {
      setMessages((prevMessages) => {
        const indexToUpdate = prevMessages.findIndex(
          (message) => message.id === id
        );
        if (indexToUpdate === -1) return prevMessages;
        return [
          ...prevMessages.slice(0, indexToUpdate),
          {
            ...prevMessages[indexToUpdate],
            ...(text !== undefined && { text }),
            ...(widget !== undefined && { widget }),
            ...(question !== undefined && { question }),
          },
          ...prevMessages.slice(indexToUpdate + 1),
        ];
      });
    },
    [setMessages]
  );

  const reset = useCallback(
    (conversationId?: string) => {
      setMessages([]);
      setLatestBotMessageId("");
      setConversationId(conversationId || uuidv4());
    },
    [pushMessage, updateMessage]
  );

  return (
    <ChatMessageState.Provider
      value={{
        messages,
        pushMessage,
        updateMessage,
        latestBotMessageId,
        reset,
        conversationId,
      }}
    >
      {children}
    </ChatMessageState.Provider>
  );
};

export const useChatMessageState = (): ChatMessageState => {
  const context = useContext(ChatMessageState);
  if (!context) {
    throw new Error(
      "useChatMessageState must be used within a ChatMessageStateProvider"
    );
  }
  return context;
};
